import { Controller } from '@hotwired/stimulus';
import * as pdfjsLib from 'pdfjs-dist';

export default class extends Controller {
  static targets = [
    'canvas',
    'drawCanvas',
    'questionField',
    'pageCount',
    'currentPage',
    'questionCollapseTrigger',
    'wrapper',
    'zoomPercentage',
    'documentContainer',
    'canvasTemplate'
  ];

  static values = {
    url: String,
    zoomMax: Number,
    zoomMin: Number,
    zoomBase: Number,
    zoomStep: Number,
    password: String,
  };

  connect () {
    this.#initialize();

    pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

    const loadingTask = pdfjsLib.getDocument({
      url: this.urlValue,
      password: this.passwordValue
    }).promise.then((data) => {
      this.viewer.pdfDoc = data;
      this.viewer.pageCount = this.viewer.pdfDoc.numPages;
      this.currentPageTarget.value = this.viewer.currentPage;
      this.pageCountTarget.textContent = this.viewer.pageCount;

      for (let i = 1; i <= this.viewer.pageCount; i++) {
        this.#renderPage(i);
      }

      this.#sizeDrawCanvas();
    }).catch((err) => {
      console.error(err.message);
    });
  }

  clearDrawCanvas () {
    this.viewer.drawCanvasContext.clearRect(0, 0, this.viewer.drawCanvas.width, this.viewer.drawCanvas.height);
  }

  handleMouseDown(e) {
    if (!this.viewer.ready || e.button !== 0) {
      return;
    }

    window.dispatchEvent(new Event('removeQuestion'));
    this.clearDrawCanvas();

    this.viewer.coordinates.startX = this.#getRelativePosition(e.offsetX);
    this.viewer.coordinates.startY = this.#getRelativePosition(e.offsetY);
    this.viewer.coordinates.width = 0;
    this.viewer.coordinates.height = 0;
    this.viewer.coordinates.page = parseInt(e.target.dataset.page, 10);
  }

  handleMouseMove(e) {
    if (!this.viewer.ready || e.buttons === 0) {
      return;
    }

    let mouseX = this.#getRelativePosition(e.offsetX);
    let mouseY = this.#getRelativePosition(e.offsetY);

    this.viewer.coordinates.width = mouseX - this.viewer.coordinates.startX;
    this.viewer.coordinates.height = mouseY - this.viewer.coordinates.startY;

    this.clearDrawCanvas();
    this.#drawRectangle();

    this.selecting = true;
  }

  handleMouseUp(e) {
    this.#normalizeCoordinates();

    if (
      this.viewer.coordinates.width > 0 &&
        this.viewer.coordinates.height > 0 &&
        this.selecting
    ) {
      window.dispatchEvent(
        new CustomEvent('newQuestion', {
          detail: {
            coordinates: [
              ...Object.values(this.viewer.coordinates),
            ],
          },
        })
      );

      if (this.hasQuestionFieldTarget) {
        this.questionFieldTarget.focus();
        window.dispatchEvent(new Event('openOffcanvas'));
      }
    }

    this.selecting = false;
  }

  handleScroll (e) {
    if (!this.viewer.ready) {
      e.preventDefault();
      return false;
    }

    const scrollTop = this.wrapperTarget.scrollTop;
    const canvases = this.#getPageCanvases();
    let cumulativeHeight = 0;
    let currentPage = 1;

    const hasScrollbar = this.wrapperTarget.scrollHeight > this.wrapperTarget.clientHeight;
    const isAtBottom = hasScrollbar &&
      Math.ceil(scrollTop) + this.wrapperTarget.clientHeight >= this.wrapperTarget.scrollHeight;

    if (isAtBottom && canvases.length > 0) {
      currentPage = parseInt(canvases[canvases.length - 1].dataset.page, 10);
    } else {
      for (const canvas of canvases) {
        if (cumulativeHeight + canvas.offsetHeight > scrollTop) {
          currentPage = parseInt(canvas.dataset.page, 10);
          break;
        }
        cumulativeHeight += canvas.offsetHeight;
        currentPage = parseInt(canvas.dataset.page, 10);
      }
    }

    this.viewer.currentPage = currentPage;
    this.currentPageTarget.value = currentPage;

    this.clearDrawCanvas();
    this.#drawRectangle();
  }

  nextPage (e) {
    e.preventDefault();

    this.#getPage(this.viewer.currentPage + 1);
  }

  previousPage(e) {
    e.preventDefault();

    this.#getPage(this.viewer.currentPage - 1);
  }

  goToPage(e) {
    e.preventDefault();

    this.#getPage(parseInt(this.currentPageTarget.value));
  }

  highlightQuestion(e) {
    if (!this.viewer.ready) {
      return;
    }

    this.#highlightDomQuestion(e.currentTarget)
  }

  #highlightDomQuestion(question) {
    const coordinates = JSON.parse(question.dataset.pdfReaderCoordinatesParam);

    this.viewer.coordinates = {
      startX: parseInt(coordinates[0], 10),
      startY: parseInt(coordinates[1], 10),
      width: parseInt(coordinates[2], 10),
      height: parseInt(coordinates[3], 10),
      page: parseInt(coordinates[4], 10),
    };

    const pageOffsetPx = this.#getPageScrollOffset(this.viewer.coordinates.page);

    window.scrollTo(0, 0);

    this.wrapperTarget.scrollTo(
      0,
      pageOffsetPx +
        this.#getAbsolutePosition(
          this.viewer.coordinates.startY +
            (this.viewer.coordinates.height / 2)
        ) -
        this.wrapperTarget.offsetHeight / 2
    );

    this.clearDrawCanvas();
    this.#drawRectangle();
  }

  // The document container animates its width (CSS transition), so we wait for
  // the transition to settle before resizing the draw canvas to the new viewport.
  resizeDrawCanvas() {
    if (!this.viewer.ready) {
      return;
    }

    this.documentContainerTarget.addEventListener(
      'transitionend',
      () => {
        this.#sizeDrawCanvas();
        this.clearDrawCanvas();
        this.#drawRectangle();
      },
      { once: true }
    );
  }

  #initialize() {
    this.viewer = {
      pdfDoc: null,
      currentPage: 1,
      pageCount: 0,
      zoom: this.zoomBaseValue,
      renderContext: null,
      ready: false,
      coordinates: {
        startX: 0,
        startY: 0,
        width: 0,
        height: 0,
        page: null,
      },
      drawCanvas: this.drawCanvasTarget,
      drawCanvasContext: this.drawCanvasTarget.getContext('2d'),
    };

    this.setZoomPercentage();
  }

  // The draw canvas is displayed 1:1 with the visible document viewport, so its
  // resolution must track the wrapper's current visible size (it changes when the
  // offcanvas collapses and the document widens).
  #sizeDrawCanvas() {
    this.viewer.drawCanvas.width = this.wrapperTarget.offsetWidth;
    this.viewer.drawCanvas.height = this.wrapperTarget.offsetHeight;

    this.viewer.drawCanvas.style.width = this.viewer.drawCanvas.width + 'px';
    this.viewer.drawCanvas.style.height = this.viewer.drawCanvas.height + 'px';
  }

  #getRelativePosition(offset) {
    return (offset * this.viewer.drawCanvas.width) / this.viewer.drawCanvas.clientWidth;
  }

  #getAbsolutePosition(offset) {
    return (offset * this.viewer.drawCanvas.clientWidth) / this.viewer.drawCanvas.width;
  }

  #getPageCanvases() {
    return this.wrapperTarget.querySelectorAll('canvas[data-page]');
  }

  // Returns the scroll offset (in px) to the top of the given page number.
  #getPageScrollOffset(pageNumber) {
    let offset = 0;
    for (const canvas of this.#getPageCanvases()) {
      if (parseInt(canvas.dataset.page, 10) >= pageNumber) break;
      offset += canvas.offsetHeight;
    }
    return offset;
  }

  #getPage (pageNumber) {
    if (!pageNumber || pageNumber < 1 || pageNumber > this.viewer.pageCount) { return null; }

    return this.#scrollToPage(pageNumber);
  }

  #scrollToPage (pageNumber) {
    this.wrapperTarget.scrollTop = this.#getPageScrollOffset(pageNumber);
    this.viewer.currentPage = pageNumber;
    this.currentPageTarget.value = pageNumber;
  }

  #renderPage (pageNumber) {
    return this.viewer.pdfDoc.getPage(pageNumber).then((page) => {
      const viewport = page.getViewport({ scale: this.viewer.zoom });
      // KEEPME: so we can bring this code back in case we don't want horizontal scrolling
      // const factor = this.wrapperTarget.offsetWidth / (viewport.width / 2);
      // const viewport = page.getViewport({ scale: this.viewer.zoom * factor });
      const canvasId = `canvas-${pageNumber}`;

      const clone = this.canvasTemplateTarget.content.cloneNode(true);
      const canvas = clone.querySelector('canvas');

      canvas.width = viewport.width;
      canvas.height = viewport.height;
      canvas.dataset.page = pageNumber;

      this.wrapperTarget.appendChild(clone);

      const displayWidth = canvas.width / 2;
      const displayHeight = canvas.height / 2;

      canvas.style.width = displayWidth + 'px';
      canvas.style.height = displayHeight + 'px';

      // to avoid canvas appearing side by side
      canvas.style.display = 'block';

      let renderContext = {
        canvasContext: canvas.getContext('2d'),
        viewport: viewport
      };

      page.render(renderContext);

      if (!this.viewer.ready && pageNumber === this.viewer.pageCount) {
        this.#documentFullyLoaded();
      }
    });
  }

  #documentFullyLoaded() {
    this.viewer.ready = true;

    let anchor = window.location.hash.substring(1);
    let question = this.questionCollapseTriggerTargets.find((q) => {
      return q.dataset.questionId === anchor
    });

    if (question === undefined) {
      this.clearDrawCanvas();
    } else {
      this.#highlightDomQuestion(question)
    }
  }

  #drawRectangle() {
    if (!this.viewer.coordinates.page) return;

    const pageOffsetPx = this.#getPageScrollOffset(this.viewer.coordinates.page);

    this.viewer.drawCanvasContext.beginPath();

    const coordinatesForPage = [
      this.viewer.coordinates.startX - this.#getRelativePosition(this.wrapperTarget.scrollLeft),
      this.viewer.coordinates.startY - this.#getRelativePosition(this.wrapperTarget.scrollTop) +
        this.#getRelativePosition(pageOffsetPx),
      this.viewer.coordinates.width,
      this.viewer.coordinates.height,
    ];

    this.viewer.drawCanvasContext.rect(...coordinatesForPage);
    this.viewer.drawCanvasContext.fillStyle = '#9befdf';
    this.viewer.drawCanvasContext.fillRect(...coordinatesForPage);
    this.viewer.drawCanvasContext.lineWidth = 3;
    this.viewer.drawCanvasContext.strokeStyle = '#9befdf';
    this.viewer.drawCanvasContext.stroke();
  }

  #normalizeCoordinates() {
    if (this.viewer.coordinates.width < 0) {
      this.viewer.coordinates.startX += this.viewer.coordinates.width;
      this.viewer.coordinates.width = -this.viewer.coordinates.width;
    }

    if (this.viewer.coordinates.height < 0) {
      this.viewer.coordinates.startY += this.viewer.coordinates.height;
      this.viewer.coordinates.height = -this.viewer.coordinates.height;
    }
  }

  #clearAndRenderPages() {
    const canvases = this.wrapperTarget.querySelectorAll('canvas[data-page]');
    canvases.forEach(canvas => canvas.remove());

    for (let pageNumber = 1; pageNumber <= this.viewer.pageCount; pageNumber++) {
      this.#renderPage(pageNumber);
    }
  }

  zoomOut(e) {
    const zoom = this.viewer.zoom - this.zoomStepValue;
    if(zoom >= this.zoomMinValue) {
      this.viewer.zoom = zoom;
      this.#clearAndRenderPages();
      this.setZoomPercentage();
    }
  }

  zoomIn(e) {
    const zoom = this.viewer.zoom + this.zoomStepValue;
    if(zoom <= this.zoomMaxValue) {
      this.viewer.zoom = zoom;
      this.#clearAndRenderPages();
      this.setZoomPercentage();
    }
  }

  setZoomPercentage() {
    if (this.hasZoomPercentageTarget) {
      const basePercentage = this.zoomBaseValue
      const currentZoom = this.viewer.zoom;
      if (basePercentage && currentZoom) {
        const currentZoomPercentage = (currentZoom * 100) / basePercentage;
        this.zoomPercentageTarget.textContent = `${Math.round(currentZoomPercentage)}%`;
      }
    }
  }
}
